import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Upload } from "lucide-react";
import clsx from "clsx";
import { Config, Deals, Handoffs, IO } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { Deal, Handoff, Stage } from "@/api/types";
import { isLost, isWon, stageColor, stageRole } from "@/lib/colors";
import { eur, fmtDayShort, pct } from "@/lib/format";
import { toast } from "@/lib/feedback";
import { daysSince, todayISO } from "@/lib/people";
import { useAuthStore } from "@/store/auth";
import { useUIStore, type DealScope } from "@/store/ui";
import TopBar from "@/components/TopBar";
import NewDealModal from "@/components/NewDealModal";
import { Avatar, Empty, Monogram, OverflowMenu, Segmented } from "@/components/ui";

type SortKey = "due" | "value" | "recent";

/** The deals list: one quiet row per deal, showing how far it has travelled and who carried it. */
export default function DealsPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { dealScope, setDealScope, openDeal } = useUIStore();
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [sort, setSort] = useState<SortKey>("due");
  const [showNew, setShowNew] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const stagesQuery = useQuery({ queryKey: ["stages"], queryFn: () => Config.listStages() });
  const dealsQuery = useQuery({
    queryKey: ["deals", { q, scope: dealScope, me: me?.id }],
    queryFn: () =>
      Deals.list({
        q: q || undefined,
        owner_user_id: dealScope === "mine" && me ? me.id : undefined,
      }),
  });
  const handoffsQuery = useQuery({
    queryKey: ["handoffs", "accepted"],
    queryFn: () => Handoffs.list("accepted"),
  });

  const stages = useMemo(
    () =>
      (stagesQuery.data ?? []).filter((s) => s.is_active).sort((a, b) => a.position - b.position),
    [stagesQuery.data],
  );
  // The track shows the path a deal travels: every active stage except the lost ones.
  const track = useMemo(() => stages.filter((s) => !isLost(s.name)), [stages]);

  const handoffsByDeal = useMemo(() => {
    const map = new Map<string, Handoff[]>();
    for (const h of handoffsQuery.data ?? []) {
      if (!map.has(h.deal_id)) map.set(h.deal_id, []);
      map.get(h.deal_id)!.push(h);
    }
    return map;
  }, [handoffsQuery.data]);

  const rows = useMemo(() => {
    const closed = (d: Deal) => isWon(d.stage) || isLost(d.stage);
    const list = (dealsQuery.data ?? []).filter((d) => showClosed || !closed(d));
    const byDue = (a: Deal, b: Deal) => (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999");
    if (sort === "due") list.sort(byDue);
    if (sort === "value") list.sort((a, b) => (b.arr ?? 0) - (a.arr ?? 0));
    if (sort === "recent") list.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return list;
  }, [dealsQuery.data, showClosed, sort]);

  const importFile = async (file: File) => {
    try {
      const r = await IO.importExcel(file, "upsert");
      toast.ok(`Imported ${r.imported} new and updated ${r.updated} existing deals.`);
      void qc.invalidateQueries({ queryKey: ["deals"] });
    } catch (e) {
      toast.err(`Import failed: ${apiErrorMessage(e)}`);
    }
  };

  const today = todayISO();
  const pos = (i: number) => (track.length <= 1 ? 0 : (i / (track.length - 1)) * 100);

  return (
    <>
      <TopBar
        title="Deals"
        onSearch={setQ}
        searchPlaceholder="Filter deals…"
        actions={
          <>
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              <Plus className="w-4 h-4" /> New deal
            </button>
            <OverflowMenu
              items={[
                {
                  label: "Import from Excel",
                  icon: <Upload className="w-4 h-4" />,
                  onClick: () => fileRef.current?.click(),
                },
                {
                  label: "Export to Excel",
                  icon: <Download className="w-4 h-4" />,
                  onClick: () =>
                    IO.downloadExport().catch((e) =>
                      toast.err(`Export failed: ${apiErrorMessage(e)}`),
                    ),
                },
              ]}
            />
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importFile(f);
                e.target.value = "";
              }}
            />
          </>
        }
      />

      <div className="px-4 py-4 md:px-6 md:py-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Segmented<DealScope>
            label="Whose deals"
            value={dealScope}
            onChange={setDealScope}
            options={[
              { value: "mine", label: "Mine" },
              { value: "all", label: "All" },
            ]}
          />
          <label className="inline-flex items-center gap-2 text-sm text-mut-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />
            Show signed and lost
          </label>
          <div className="flex-1" />
          <label className="text-sm text-mut-500 flex items-center gap-2">
            Sort by
            <select
              className="input !w-auto !py-1 text-sm"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              <option value="due">Next due</option>
              <option value="value">Value</option>
              <option value="recent">Recently updated</option>
            </select>
          </label>
        </div>

        <div className="panel overflow-x-auto">
          <div className="md:min-w-[860px]">
            {/* Column header with stage labels laid out along the track */}
            <div className="hidden md:grid grid-cols-[240px_minmax(0,1fr)_210px] gap-6 px-4 pt-3 pb-2 border-b border-fg/[0.06]">
              <span className="section-title">Deal</span>
              {/* Labels sit over their tick and may wrap to two lines, so long
                  stage names ("Advanced discussion") never collide. */}
              <div className="relative h-8">
                {track.map((s, i) => {
                  const slot = track.length > 1 ? 100 / (track.length - 1) : 100;
                  const first = i === 0;
                  const last = i === track.length - 1 && track.length > 1;
                  return (
                    <span
                      key={s.id}
                      title={s.name}
                      className={clsx(
                        "absolute top-0 text-2xs font-medium leading-[13px] line-clamp-2",
                        first ? "text-left" : last ? "text-right" : "text-center",
                      )}
                      style={{
                        left: `${pos(i)}%`,
                        width: `calc(${first || last ? slot / 2 : slot}% - 4px)`,
                        transform: first ? "none" : last ? "translateX(-100%)" : "translateX(-50%)",
                        color: stageColor(s.name, s.color),
                      }}
                    >
                      {s.name}
                    </span>
                  );
                })}
              </div>
              <span className="section-title">Next</span>
            </div>

            {dealsQuery.isLoading ? (
              <p className="p-6 text-md text-mut-500">Loading…</p>
            ) : rows.length === 0 ? (
              <Empty
                title={
                  q
                    ? "No deals match that filter"
                    : dealScope === "mine"
                      ? "You don't own any open deals"
                      : "No open deals yet"
                }
              >
                {dealScope === "mine"
                  ? "Switch to All to see the team's deals, or create one."
                  : "Create a deal or import your Excel pipeline."}
              </Empty>
            ) : (
              <ul>
                {rows.map((d) => (
                  <DealRow
                    key={d.id}
                    deal={d}
                    track={track}
                    stages={stages}
                    handoffs={handoffsByDeal.get(d.id) ?? []}
                    pos={pos}
                    today={today}
                    onOpen={() => openDeal(d.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {showNew && <NewDealModal stages={stages} onClose={() => setShowNew(false)} />}
    </>
  );
}

function DealRow({
  deal: d,
  track,
  stages,
  handoffs,
  pos,
  today,
  onOpen,
}: {
  deal: Deal;
  track: Stage[];
  stages: Stage[];
  handoffs: Handoff[];
  pos: (i: number) => number;
  today: string;
  onOpen: () => void;
}) {
  const idx = track.findIndex((s) => s.name === d.stage);
  const lost = isLost(d.stage) || idx < 0;
  const warn = !lost && (d.is_stuck || d.is_cold);
  const role = stageRole(
    d.stage,
    stages.findIndex((s) => s.name === d.stage),
  );
  const here = lost ? 100 : pos(idx);

  // Who carried it: the first owner at the start, then each accepted handoff
  // at the stage it happened in. Nudge batons apart when they'd overlap.
  const batons: { name: string | null; at: number }[] = [];
  const firstOwner = handoffs[0]?.from_name ?? d.primary_owner;
  if (firstOwner) batons.push({ name: firstOwner, at: 0 });
  for (const h of handoffs) {
    const i = track.findIndex((s) => s.name === h.stage);
    let at = i < 0 ? 0 : pos(i);
    while (batons.some((b) => Math.abs(b.at - at) < 4)) at += 4;
    batons.push({ name: h.to_name, at: Math.min(at, 100) });
  }

  const quietDays = daysSince(d.last_contact);
  const late = d.due_date != null && d.due_date <= today;

  return (
    <li>
      <button
        onClick={onOpen}
        className="w-full grid grid-cols-1 gap-2.5 md:grid-cols-[240px_minmax(0,1fr)_210px] md:gap-6 items-center px-4 py-3.5 text-left border-b border-fg/[0.04] last:border-b-0 hover:bg-fg/[0.03] transition-colors"
      >
        <span className="flex items-center gap-3 min-w-0">
          <Monogram name={d.lead} role={role} />
          <span className="min-w-0">
            <span className="block text-md font-medium truncate">{d.lead}</span>
            <span className="block text-sm text-mut-500 truncate tabular-nums">
              {eur(d.arr, { short: true })} · {lost ? d.stage : pct(d.probability)}
              {d.account_name ? ` · ${d.account_name}` : ""}
            </span>
          </span>
        </span>

        <span className="block">
          {/* Phones have no stage header over the tracks, so name the stage inline. */}
          <span
            className="md:hidden mb-2 block text-2xs font-semibold"
            style={{ color: lost ? undefined : stageColor(d.stage) }}
          >
            {d.stage}
            {d.days_in_stage != null && (
              <span className="font-normal text-mut-500"> · {d.days_in_stage} d</span>
            )}
          </span>
          <span className="track block" aria-label={`Stage: ${d.stage}`}>
            <span
              className={clsx("track-fill", warn && "is-warn", lost && "is-lost")}
              style={{ ["--p" as string]: `${here}%` }}
            />
            {batons.map((b, i) => (
              <span key={i} className="track-baton" style={{ left: `${b.at}%` }}>
                <Avatar name={b.name} size={18} className="ring-2 ring-ink-900" />
              </span>
            ))}
            <span
              className="track-here"
              style={{
                left: `${here}%`,
                ["--c" as string]: lost
                  ? "var(--mut-500)"
                  : warn
                    ? "var(--amber)"
                    : `var(--${role})`,
              }}
            />
          </span>
        </span>

        <span className="min-w-0 text-sm">
          {warn && d.is_cold && quietDays != null ? (
            <span className="font-medium text-accent-amber">Quiet for {quietDays} days</span>
          ) : d.next_action ? (
            <>
              <span className="block font-medium text-mut-100 truncate">{d.next_action}</span>
              {d.due_date && (
                <span className={clsx(late ? "text-accent-pink font-medium" : "text-mut-500")}>
                  {d.due_date < today
                    ? `Overdue · ${fmtDayShort(d.due_date)}`
                    : d.due_date === today
                      ? "Today"
                      : fmtDayShort(d.due_date)}
                </span>
              )}
            </>
          ) : warn ? (
            <span className="font-medium text-accent-amber">
              In {d.stage} for {d.days_in_stage} days
            </span>
          ) : (
            <span className="text-mut-500">No next step set</span>
          )}
        </span>
      </button>
    </li>
  );
}
